import { useMemo } from 'react';
import { i18n } from '@/shared/i18n';
import { useQueries } from '@tanstack/react-query';
import type { TaxonomyField } from '@/entities/taxonomy';
import {
  taxonomyListDetailQueryOptions,
  type ResolvedList,
  type TaxonomyListDetail,
} from '@/entities/taxonomy-list';
import {
  resolveBoundColumnPair,
  resolveBoundListVersion,
  resolveListOption,
} from '@inference.engine/taxonomy/web';

/**
 * Flattens one list version's rows into value/code pairs for a binding.
 * A missing code column means the display value is emitted verbatim.
 */
function resolveFromList(
  list: TaxonomyListDetail,
  ref: NonNullable<TaxonomyField['listRef']>,
): ResolvedList {
  // The SAME rule the server mints by. This used to resolve a following binding
  // to `currentVersionId` — the head, which can be a draft — so the grid offered
  // options the generator could not find, and a value renamed on that draft
  // minted as its raw display string into a permanent code.
  const resolved = resolveBoundListVersion(list, ref);
  const version = resolved?.version;

  if (!version) {
    return {
      values: [],
      listName: list.name,
      listId: list.id,
      versionId: null,
      follows: !ref.listVersionId,
      versionNumber: null,
      // A list with no versions at all is one the server's publish gate also
      // refuses, so reporting it as fine here meant the dialog said go and the
      // API said no. A broken PIN is different: the list has published versions,
      // this binding just names one that is gone.
      unpublished: !ref.listVersionId,
      publishable: false,
      error: i18n.t('knowledge:taxonomy.useResolvedListValues.versionNotFound'),
    };
  }

  // A following binding reads the published version, so a higher-numbered draft
  // is work the schema is deliberately NOT reading — worth disclosing before a
  // publish rather than surprising someone afterwards.
  // DRAFT specifically. A following binding already resolves to the highest
  // published version, so anything above it is either a draft or an archived
  // one — and an archived version is work somebody deliberately retired, not
  // work the schema is missing out on.
  const newerDraft = resolved.follows
    ? list.versions
        .filter((v) => v.status === 'draft' && v.versionNumber > version.versionNumber)
        .sort((a, b) => b.versionNumber - a.versionNumber)[0]
    : undefined;

  const hasValueColumn = version.columns.some((c) => c.id === ref.valueColumnId);
  /**
   * TRIMMED, matching the generator.
   *
   * This used to trim only for the emptiness test and keep the untrimmed string
   * as the value, while the server trims both. Nothing trims cells on write, so a
   * cell holding `"US "` offered `"US "` in the grid; the submitted value went to
   * the API untrimmed and was looked up in a map keyed by the trimmed `"US"`;
   * the lookup missed and the code came out as the raw `"US "`. One string on
   * screen, a different one in a tracking code that is permanent once minted.
   */
  const blankCodeValues: string[] = [];
  const values = version.rows.flatMap((row) => {
    const option = resolveListOption(
      row.cells[ref.valueColumnId],
      ref.codeColumnId ? row.cells[ref.codeColumnId] : undefined,
    );
    if (!option) return [];
    // A field that reads no code column has no blank code: its values double as codes by choice.
    if (ref.codeColumnId && option.codeWasBlank) blankCodeValues.push(option.value);
    return [{ value: option.value, code: option.code }];
  });

  const suggestedCode = ref.codeColumnId
    ? undefined
    : resolveBoundColumnPair(version.columns)?.codeColumn;
  const unusedCodeColumn =
    suggestedCode &&
    suggestedCode.id !== ref.valueColumnId &&
    // Only a code an option can use: a code on a row with no value is never offered.
    version.rows.some(
      (row) =>
        resolveListOption(row.cells[ref.valueColumnId], row.cells[suggestedCode.id])
          ?.codeWasBlank === false,
    )
      ? suggestedCode
      : undefined;

  return {
    values,
    blankCodeValues,
    ...(unusedCodeColumn ? { unusedCodeColumnName: unusedCodeColumn.name } : {}),
    listName: list.name,
    listId: list.id,
    versionId: version.id,
    follows: resolved.follows,
    versionNumber: version.versionNumber,
    unpublished: version.status === 'draft',
    // Head too, not just draft: see the field's doc. A pinned non-head draft is
    // `unpublished` and cannot be published, which is the whole point of the
    // distinction between these two.
    publishable: version.status === 'draft' && version.id === list.currentVersionId,
    ...(newerDraft ? { newerDraftVersionNumber: newerDraft.versionNumber } : {}),
    ...(hasValueColumn
      ? {}
      : { error: i18n.t('knowledge:taxonomy.useResolvedListValues.boundColumnNoLongerExists') }),
  };
}

/**
 * Resolves every `listRef` in `fields` into displayable values.
 *
 * Fetches only the lists actually referenced (deduped), so a schema with no
 * bindings issues no requests. Returns a map keyed by FIELD id — the builder
 * renders from this while `field.values` stays the authoritative persisted
 * state, which is what keeps autosave from writing a list's values back into
 * the schema.
 */
export function useResolvedListValues(fields: TaxonomyField[]): Record<string, ResolvedList> {
  const refs = useMemo(
    () => fields.filter((f) => f.listRef?.listId).map((f) => ({ fieldId: f.id, ref: f.listRef! })),
    [fields],
  );

  const listIds = useMemo(() => [...new Set(refs.map((r) => r.ref.listId))], [refs]);

  const results = useQueries({
    queries: listIds.map((id) => ({ ...taxonomyListDetailQueryOptions(id), enabled: !!id })),
  });

  return useMemo(() => {
    const byListId = new Map<string, TaxonomyListDetail>();
    const stillFetching = new Set<string>();
    listIds.forEach((id, i) => {
      const data = results[i]?.data;
      if (data) byListId.set(id, data);
      else if (results[i]?.isPending) stillFetching.add(id);
    });

    const out: Record<string, ResolvedList> = {};
    for (const { fieldId, ref } of refs) {
      const list = byListId.get(ref.listId);
      if (list) {
        out[fieldId] = resolveFromList(list, ref);
      } else if (stillFetching.has(ref.listId)) {
        out[fieldId] = {
          values: [],
          listName: i18n.t('knowledge:taxonomy.useResolvedListValues.loading'),
          listId: ref.listId,
          versionId: null,
          follows: !ref.listVersionId,
          versionNumber: null,
          unpublished: false,
          publishable: false,
          pending: true,
        };
      } else {
        out[fieldId] = {
          values: [],
          listName: i18n.t('knowledge:taxonomy.useResolvedListValues.unknownList'),
          listId: ref.listId,
          versionId: null,
          follows: !ref.listVersionId,
          versionNumber: null,
          unpublished: false,
          publishable: false,
          error: i18n.t('knowledge:taxonomy.useResolvedListValues.listUnavailable'),
        };
      }
    }
    return out;
    // `results` is a new array each render; key off the resolved data instead.
    // The pending flag has to be in the key too, or the map keeps reporting
    // "unavailable" after the request settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, listIds, results.map((r) => `${r.dataUpdatedAt}:${r.isPending}`).join(',')]);
}
